import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/digital-tools.svg";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsappButton";
import { Link } from "react-router-dom";

const IPadSystem: React.FC = () => {
  const { t } = useLanguage();

  const appFeatures = t("ipadSystem.appFeatures.items") as Array<any>;
  const whyWeChooseThis = t("ipadSystem.whyChoose.items") as Array<any>;
  const privacyFeatures = t("ipadSystem.privacy.items") as Array<any>;
  const technicalBenefits = t("ipadSystem.technical.benefits") as Array<string>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      {/* Hero Section */}
      <div className="relative z-10">
        <div className="relative overflow-hidden hero-section-h">
          {/* Decorative SVGs */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-[-50px] right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[350px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {t("ipadSystem.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-center text-black opacity-90 max-w-4xl mx-auto leading-relaxed">
              {t("ipadSystem.hero.subtitle")}
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 md:p-12 mb-16">
              <div className="mb-6">
                <h2 className="text-3xl font-semibold text-black mb-6 leading-tight">
                  {t("ipadSystem.intro.title")}
                </h2>
                <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                  {t("ipadSystem.intro.text1")}
                </p>
                <p className="text-lg text-neutral-800 leading-relaxed">
                  {t("ipadSystem.intro.text2")}
                </p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-8 border border-amber-100">
                <h3 className="text-2xl font-semibold text-black mb-6">
                  {t("ipadSystem.intro.supportTitle")}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t("ipadSystem.intro.supportText")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* App Features Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {t("ipadSystem.appFeatures.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("ipadSystem.appFeatures.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto justify-center">
            {Array.isArray(appFeatures) &&
              appFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className="text-center mb-6">
                    <div className="text-5xl mb-4">{feature.icon}</div>
                    <h3 className="text-2xl font-semibold text-black mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-neutral-800 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-black mb-4">
                      {t("ipadSystem.appFeatures.whatYouSee")}
                    </h4>
                    <ul className="space-y-2">
                      {feature.benefits &&
                        feature.benefits.map((benefit: string, idx: number) => (
                          <li key={idx} className="flex items-start space-x-2 text-left">
                            <div className="w-2 h-2 bg-[#492A19] rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-sm text-neutral-800">
                              {benefit}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Why We Choose This Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {t("ipadSystem.whyChoose.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("ipadSystem.whyChoose.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {Array.isArray(whyWeChooseThis) &&
              whyWeChooseThis.map((reason, index) => (
                <div key={index} className="group">
                  <div
                    className={`${reason.color} rounded-2xl p-8 text-white h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                  >
                    <div className="text-3xl mb-6">{reason.icon}</div>
                    <h3 className="text-2xl font-semibold mb-4">
                      {reason.title}
                    </h3>
                    <p className="text-lg opacity-90 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Privacy and Security Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {t("ipadSystem.privacy.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("ipadSystem.privacy.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="bg-amber-600 rounded-3xl shadow-2xl p-12 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-black opacity-5"></div>
              <div className="relative">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Array.isArray(privacyFeatures) &&
                    privacyFeatures.map((feature, index) => (
                      <div
                        key={index}
                        className="bg-[#FBEACD] bg-opacity-20 rounded-xl p-6 backdrop-blur-sm text-center"
                      >
                        <div className="text-4xl mb-4">{feature.icon}</div>
                        <h3 className="text-lg font-semibold mb-3">
                          {feature.title}
                        </h3>
                        <p className="text-sm opacity-90 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    ))}
                </div>
                <div className="text-center mt-8">
                  <p className="text-lg opacity-90">
                    {t("ipadSystem.privacy.note")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Details Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-semibold text-black mb-6">
                    {t("ipadSystem.technical.title")}
                  </h3>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {t("ipadSystem.technical.text1")}
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {t("ipadSystem.technical.text2")}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-2xl p-8">
                  <h4 className="text-xl font-semibold text-black mb-6">
                    {t("ipadSystem.technical.benefitsTitle")}
                  </h4>
                  <ul className="space-y-4">
                    {Array.isArray(technicalBenefits) &&
                      technicalBenefits.map((benefit, idx) => (
                        <li
                          key={idx}
                          className="flex items-center justify-center space-x-3"
                        >
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 py-16 font-child">
          <div className="text-center">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-semibold text-black mb-4">
                {t("ipadSystem.cta.title")}
              </h3>
              <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t("ipadSystem.cta.subtitle")}
              </p>
              <Link
                to="/parent-collaboration"
                className="bg-[#FBEACD] text-[#492A19] px-8 py-3 rounded-full font-semibold text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("ipadSystem.cta.button")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default IPadSystem;
